import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  Monitor,
  CheckCircle2,
  Info,
  Camera,
  AppWindow,
  ClipboardList,
  Usb,
  AlertTriangle,
  Loader2,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../components/ui/modal';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getWorkerDetails,
  getScreenshots,
  getAppUsage,
  getClipboardEvents,
  getUSBEvents,
  getIncidents,
  getRiskSummary,
  getActivityTimeline,
  getClipboardImage,
} from '../services/api';
import RiskMethodology from '../components/Dashboard/RiskMethodology';
import SystemHealth from '../components/Dashboard/SystemHealth';
import ThreatMap from '../components/Dashboard/ThreatMap';
import TopRisks from '../components/Dashboard/TopRisks';
import LiveEventFeed from '../components/Dashboard/LiveEventFeed';
import AutoRefreshControl from '../components/AutoRefreshControl';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StatTile } from '../components/ui/stat-tile';
import { Progress } from '../components/ui/progress';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { cn } from '../lib/utils';

const STATUS_VARIANT = {
  online: 'success',
  approved: 'secondary',
  pending: 'warning',
  offline: 'outline',
  rejected: 'destructive',
};

function severityVariant(sev) {
  if (sev === 'critical') return 'destructive';
  if (sev === 'high') return 'warning';
  if (sev === 'medium') return 'default';
  return 'outline';
}

function riskBadge(score) {
  if (score >= 70) return 'destructive';
  if (score >= 40) return 'warning';
  return 'secondary';
}

function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString();
}

function WorkerDetailPage() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [currentTab, setCurrentTab] = useState('screenshots');
  const [loading, setLoading] = useState(true);

  const [screenshots, setScreenshots] = useState([]);
  const [appUsage, setAppUsage] = useState([]);
  const [clipboardEvents, setClipboardEvents] = useState([]);
  const [usbEvents, setUSBEvents] = useState([]);
  const [incidents, setIncidents] = useState([]);

  const [stats, setStats] = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);
  const [activities, setActivities] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);

  const goToEvent = (page, id, e) => {
    if (e) e.stopPropagation();
    navigate(`/worker/${workerId}/${page}?event=${id}`);
  };

  const loadWorkerData = async () => {
    if (!worker) setLoading(true);
    try {
      const workerData = await getWorkerDetails(workerId);
      setWorker(workerData);

      const [screenshotsRes, appRes, clipboardRes, usbRes, incidentsRes] =
        await Promise.all([
          getScreenshots(50, 0, workerId).catch(() => null),
          getAppUsage(null, workerId ? parseInt(workerId) : null).catch(() => null),
          getClipboardEvents(200, workerId ? parseInt(workerId) : null).catch(() => null),
          getUSBEvents(200, workerId ? parseInt(workerId) : null).catch(() => null),
          getIncidents(null, 200, workerId ? parseInt(workerId) : null).catch(() => null),
        ]);

      // Most backend routes wrap as { success, data: [...] }, but a couple
      // (older ones) return the array directly. Accept both shapes.
      const unwrap = (res) => {
        const body = res?.data;
        if (Array.isArray(body)) return body;
        if (Array.isArray(body?.data)) return body.data;
        return [];
      };

      // Sort each list by risk score (highest first), with timestamp as
      // a tiebreaker so the most recent of equal-risk events wins.
      const byRiskDesc = (list, riskKey = "risk_score") =>
        [...list].sort((a, b) => {
          const ra = a?.[riskKey] || 0;
          const rb = b?.[riskKey] || 0;
          if (rb !== ra) return rb - ra;
          const ta = new Date(a?.timestamp || a?.start_time || 0).getTime();
          const tb = new Date(b?.timestamp || b?.start_time || 0).getTime();
          return tb - ta;
        });

      setScreenshots(byRiskDesc(unwrap(screenshotsRes)));
      setAppUsage(byRiskDesc(unwrap(appRes)));
      setClipboardEvents(byRiskDesc(unwrap(clipboardRes)));
      setUSBEvents(byRiskDesc(unwrap(usbRes)));
      setIncidents(byRiskDesc(unwrap(incidentsRes), "total_risk_score"));

      try {
        const [statsRes, riskRes, activityRes] = await Promise.all([
          axios
            .get(`http://localhost:8765/api/dashboard/worker/${workerId}/stats`)
            .catch(() => ({ data: { data: {} } })),
          getRiskSummary(workerId ? parseInt(workerId) : null).catch(() => ({
            data: { data: {} },
          })),
          getActivityTimeline(24, workerId ? parseInt(workerId) : null).catch(() => ({
            data: { data: [] },
          })),
        ]);
        setStats(statsRes?.data?.data || {});
        setRiskSummary(riskRes?.data?.data || {});
        setActivities(activityRes?.data?.data || []);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      }
    } catch (error) {
      console.error('Failed to load worker data:', error);
      setScreenshots([]);
      setAppUsage([]);
      setClipboardEvents([]);
      setUSBEvents([]);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  };

  const autoRefresh = useAutoRefresh(loadWorkerData, 30000, true);

  useEffect(() => {
    loadWorkerData();
  }, [workerId]);

  if (loading || !worker) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const avgRisk = riskSummary?.average_risk_score ?? 0;
  const riskTone =
    avgRisk > 70 ? 'destructive' : avgRisk > 40 ? 'warning' : 'success';
  const riskToneClass = {
    destructive: 'bg-destructive',
    warning: 'bg-warning',
    success: 'bg-success',
  }[riskTone];

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/workers')}>
          <ArrowLeft />
          Back to Workers
        </Button>
        <AutoRefreshControl {...autoRefresh} />
      </div>

      {/* Worker identity card */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-6 p-6">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Monitor className="h-9 w-9" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">
                {worker.worker_name}
              </h2>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge variant={STATUS_VARIANT[worker.status] || 'outline'} className="capitalize">
                  <CheckCircle2 className="h-3 w-3" />
                  {worker.status}
                </Badge>
                <Badge variant="outline">
                  <Info className="h-3 w-3" />
                  Version {worker.client_version}
                </Badge>
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Computer</dt>
                <dd className="text-foreground">{worker.computer_name}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">IP Address</dt>
                <dd className="font-mono text-xs text-foreground">{worker.ip_address}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">OS</dt>
                <dd className="text-foreground">{worker.operating_system}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wider text-muted-foreground">Last Seen</dt>
                <dd className="text-foreground">{formatDate(worker.last_seen)}</dd>
              </div>
            </dl>
          </div>
        </CardContent>
      </Card>

      {/* Risk + system health + activity summary + recent */}
      <div className="grid gap-4 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Risk Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {riskSummary ? (
              <>
                <div className="flex items-end justify-between">
                  <span className="text-4xl font-bold tabular-nums text-foreground">
                    {avgRisk.toFixed(1)}
                  </span>
                  <Badge
                    variant={
                      avgRisk > 70 ? 'destructive' : avgRisk > 40 ? 'warning' : 'success'
                    }
                  >
                    {avgRisk > 70 ? 'High Risk' : avgRisk > 40 ? 'Medium Risk' : 'Low Risk'}
                  </Badge>
                </div>
                <Progress
                  value={Math.min(avgRisk, 100)}
                  indicatorClassName={cn('transition-all', riskToneClass)}
                />
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <div className="text-xs text-muted-foreground">High risk</div>
                    <div className="text-lg font-semibold text-destructive">
                      {riskSummary.high_risk_count || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Total events</div>
                    <div className="text-lg font-semibold text-foreground">
                      {riskSummary.total_events || 0}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Loading risk data...</div>
            )}
          </CardContent>
        </Card>

        <SystemHealth />

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Activity Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {stats ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Screenshots</div>
                  <div className="text-lg font-semibold text-foreground">
                    {stats.total_screenshots || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Incidents</div>
                  <div className="text-lg font-semibold text-destructive">
                    {stats.total_incidents || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Clipboard</div>
                  <div className="text-lg font-semibold text-foreground">
                    {stats.total_clipboard || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">USB events</div>
                  <div className="text-lg font-semibold text-foreground">
                    {stats.total_usb || 0}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Loading stats...</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activities.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recent activity</p>
            ) : (
              <ul className="divide-y divide-border">
                {activities.slice(0, 4).map((activity, idx) => (
                  <li key={idx} className="py-2">
                    <div className="text-sm text-foreground">{activity.type}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(activity.timestamp).toLocaleTimeString()}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Threat map + top risks */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ThreatMap activities={activities} />
        <TopRisks activities={activities} />
      </div>

      <LiveEventFeed activities={activities} />

      <div className="grid gap-4 lg:grid-cols-2">
        <RiskMethodology />
      </div>

      {/* Monitoring data tiles */}
      <div>
        <h2 className="mb-3 text-lg font-semibold tracking-tight text-foreground">
          Monitoring Data
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatTile label="Screenshots" value={screenshots.length} icon={Camera} tone="primary" />
          <StatTile label="App Usage" value={appUsage.length} icon={AppWindow} tone="primary" />
          <StatTile label="Clipboard" value={clipboardEvents.length} icon={ClipboardList} tone="primary" />
          <StatTile label="USB Events" value={usbEvents.length} icon={Usb} tone="warning" />
          <StatTile label="Incidents" value={incidents.length} icon={AlertTriangle} tone="destructive" />
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <TabsList>
              <TabsTrigger value="screenshots">Screenshots</TabsTrigger>
              <TabsTrigger value="apps">Applications</TabsTrigger>
              <TabsTrigger value="clipboard">Clipboard</TabsTrigger>
              <TabsTrigger value="usb">USB Activity</TabsTrigger>
              <TabsTrigger value="incidents">Incidents</TabsTrigger>
            </TabsList>

            <TabsContent value="screenshots">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Application</TableHead>
                    <TableHead>Window</TableHead>
                    <TableHead>Risk Score</TableHead>
                    <TableHead>PII</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {screenshots.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                        No screenshots recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    screenshots.map((s) => (
                      <TableRow
                        key={s.id}
                        className="cursor-pointer"
                        onClick={() => goToEvent('screenshots', s.id)}
                      >
                        <TableCell className="text-muted-foreground">{formatDate(s.timestamp)}</TableCell>
                        <TableCell>{s.application || 'N/A'}</TableCell>
                        <TableCell>{s.active_window || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant={riskBadge(s.risk_score)}>{s.risk_score}</Badge>
                        </TableCell>
                        <TableCell>
                          {s.has_pii ? <Badge variant="warning">Yes</Badge> : 'No'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="apps">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Application</TableHead>
                    <TableHead>Window Title</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appUsage.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                        No application usage recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    appUsage.map((app, idx) => (
                      <TableRow
                        key={idx}
                        className="cursor-pointer"
                        onClick={() => navigate(`/worker/${workerId}/applications`)}
                      >
                        <TableCell className="font-medium">{app.application}</TableCell>
                        <TableCell>{app.window_title || 'N/A'}</TableCell>
                        <TableCell className="text-muted-foreground">{formatDate(app.start_time)}</TableCell>
                        <TableCell>{Math.floor(app.duration / 60)} min</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="clipboard">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[88px]">Preview</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Content</TableHead>
                    <TableHead>Risk Score</TableHead>
                    <TableHead>PII</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clipboardEvents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                        No clipboard events recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    clipboardEvents.map((e) => {
                      const isImage = e.content_type === 'image';
                      return (
                        <TableRow
                          key={e.id}
                          className="cursor-pointer"
                          onClick={() => goToEvent('clipboard', e.id)}
                        >
                          <TableCell onClick={(ev) => ev.stopPropagation()}>
                            {isImage && e.image_file ? (
                              <img
                                src={getClipboardImage(e.id)}
                                alt="Clipboard"
                                onClick={() => setPreviewImage(e)}
                                className={cn(
                                  'h-14 w-14 cursor-zoom-in rounded-md object-cover ring-1',
                                  e.has_pii ? 'ring-destructive/60' : 'ring-border'
                                )}
                                onError={(ev) => {
                                  ev.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="flex h-14 w-14 items-center justify-center rounded-md bg-muted text-muted-foreground">
                                {isImage ? (
                                  <ImageIcon className="h-5 w-5" />
                                ) : (
                                  <ClipboardList className="h-5 w-5" />
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{formatDate(e.timestamp)}</TableCell>
                          <TableCell>
                            <Badge variant={isImage ? 'secondary' : 'outline'} className="capitalize">
                              {isImage && <ImageIcon className="h-3 w-3" />}
                              {e.content_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md truncate">
                            {e.content_preview || (isImage ? 'Image' : 'N/A')}
                          </TableCell>
                          <TableCell>
                            <Badge variant={riskBadge(e.risk_score)}>{e.risk_score}</Badge>
                          </TableCell>
                          <TableCell>
                            {e.has_pii ? <Badge variant="warning">Yes</Badge> : 'No'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="usb">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Event Type</TableHead>
                    <TableHead>Device Name</TableHead>
                    <TableHead>Files Transferred</TableHead>
                    <TableHead>Risk Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usbEvents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                        No USB events recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    usbEvents.map((e) => (
                      <TableRow
                        key={e.id}
                        className="cursor-pointer"
                        onClick={() => goToEvent('usb', e.id)}
                      >
                        <TableCell className="text-muted-foreground">{formatDate(e.timestamp)}</TableCell>
                        <TableCell>{e.event_type}</TableCell>
                        <TableCell>{e.device_name || 'N/A'}</TableCell>
                        <TableCell>{e.files_transferred || 0}</TableCell>
                        <TableCell>
                          <Badge variant={riskBadge(e.risk_score)}>{e.risk_score}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            <TabsContent value="incidents">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Risk Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                        No incidents recorded yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    incidents.map((i) => (
                      <TableRow
                        key={i.id}
                        className="cursor-pointer"
                        onClick={() => goToEvent('incidents', i.id)}
                      >
                        <TableCell className="text-muted-foreground">{formatDate(i.timestamp)}</TableCell>
                        <TableCell>{i.incident_type}</TableCell>
                        <TableCell>
                          <Badge variant={severityVariant(i.severity)} className="capitalize">
                            {i.severity}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-md truncate">{i.description}</TableCell>
                        <TableCell>
                          <Badge variant={riskBadge(i.total_risk_score)}>
                            {i.total_risk_score}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Clipboard image preview modal */}
      <Modal open={!!previewImage} onClose={() => setPreviewImage(null)} className="max-w-3xl">
        <ModalHeader>
          <ModalTitle>Clipboard Image</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {previewImage && (
            <>
              <div className="flex justify-center">
                <img
                  src={getClipboardImage(previewImage.id)}
                  alt="Clipboard"
                  className="max-h-[70vh] max-w-full object-contain"
                />
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {formatDate(previewImage.timestamp)}
                </span>
                {previewImage.source_application && (
                  <> · Source: {previewImage.source_application}</>
                )}
                {' · '}Risk:{' '}
                <Badge variant={riskBadge(previewImage.risk_score)}>{previewImage.risk_score}</Badge>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setPreviewImage(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default WorkerDetailPage;
